/**
 * Hand-rolled runtime guards for API responses.
 *
 * Deliberately lenient: unknown extra fields are ignored and most optional
 * fields default rather than fail. A guard throws only when a payload is
 * structurally unusable (wrong top-level shape, missing identity fields).
 * Shapes are pinned to the server's serde output — the server is canonical.
 */
import {
  DUCT_ROLES,
  DUCT_SHAPES,
  DUCT_SYSTEMS,
  EXCLUSION_TIERS,
  FINDING_SEVERITIES,
  READINESS_STATUSES,
  REVIEW_STATUSES,
  type ApiFinding,
  type ApiProject,
  type ApiSpace,
  type ApiStorey,
  type AutoAssignResult,
  type DiffEntry,
  type ExclusionCounts,
  type ExclusionTier,
  type FindingCitation,
  type FindingSeverity,
  type DuctRole,
  type DuctShape,
  type DuctSystem,
  type GenerateAccepted,
  type GenerationGate,
  type EquipmentKind,
  type MepAirflow,
  type MepCircuit,
  type MepDevice,
  type MepDuct,
  type MepEquipment,
  type MepFitting,
  type MepSystem,
  type MepTerminal,
  type MepZone,
  type ModelCreated,
  type ModelDiff,
  type ReadinessCapability,
  type ReadinessReport,
  type ReadinessStatus,
  type ReviewItem,
  type ReviewStatus,
  type ScrapeRunAccepted,
  type ScraperSource,
} from "./types";
import type { PlanPoint } from "../viewer/ifc/types";

/** Thrown when a response body does not match the expected contract shape. */
export class ApiShapeError extends Error {
  constructor(what: string, value: unknown) {
    super(`Unexpected API response shape for ${what}: ${preview(value)}`);
    this.name = "ApiShapeError";
  }
}

function preview(value: unknown): string {
  try {
    const s = JSON.stringify(value);
    return s === undefined ? String(value) : s.slice(0, 120);
  } catch {
    return String(value);
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function str(v: unknown, fallback: string): string {
  return typeof v === "string" ? v : fallback;
}

function strOrNull(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}

function numOrNull(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function bool(v: unknown, fallback: boolean): boolean {
  return typeof v === "boolean" ? v : fallback;
}

/**
 * Seeds are JS-safe integers on the wire (the server bounds rolled seeds
 * below 2^53). Anything else cannot round-trip through a pinned-seed echo —
 * reject rather than display/replay a lossy value.
 */
function safeSeed(v: unknown): number | null {
  return typeof v === "number" && Number.isSafeInteger(v) ? v : null;
}

function stringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is string => typeof x === "string");
}

export function guardProject(v: unknown): ApiProject {
  if (!isRecord(v) || typeof v.id !== "string") {
    throw new ApiShapeError("project", v);
  }
  return {
    id: v.id,
    name: str(v.name, ""),
    description: str(v.description, ""),
    created_at: numOrNull(v.created_at) ?? 0,
    updated_at: numOrNull(v.updated_at) ?? 0,
  };
}

export function guardProjects(v: unknown): ApiProject[] {
  if (!Array.isArray(v)) throw new ApiShapeError("project list", v);
  return v.map(guardProject);
}

export function guardModelCreated(v: unknown): ModelCreated {
  if (!isRecord(v)) throw new ApiShapeError("model upload response", v);
  // Tolerate either `model_id` (contract) or `id`.
  const id = typeof v.model_id === "string" ? v.model_id : v.id;
  if (typeof id !== "string") {
    throw new ApiShapeError("model upload response", v);
  }
  return { model_id: id };
}

function footprint(v: unknown): PlanPoint[] | null {
  if (!Array.isArray(v) || v.length < 3) return null;
  const points: PlanPoint[] = [];
  for (const p of v) {
    if (!Array.isArray(p) || typeof p[0] !== "number" || typeof p[1] !== "number") {
      return null;
    }
    points.push([p[0], p[1]]);
  }
  return points;
}

function exclusion(v: unknown): ExclusionTier {
  return EXCLUSION_TIERS.includes(v as ExclusionTier) ? (v as ExclusionTier) : "none";
}

function stringMap(v: unknown): Record<string, string> {
  if (!isRecord(v)) return {};
  const out: Record<string, string> = {};
  for (const [k, val] of Object.entries(v)) {
    if (typeof val === "string") out[k] = val;
  }
  return out;
}

/** Conditioning classification (ENG-15038): a `{kind, reason?}` tagged object, or
 *  null (pre-migration / not-yet-reparsed rows, or any unrecognized shape). */
function conditioningClass(v: unknown): ApiSpace["conditioning_class"] {
  if (!isRecord(v)) return null;
  if (v.kind === "conditioned") return { kind: "conditioned" };
  if (v.kind === "non_conditioned" && typeof v.reason === "string") {
    return { kind: "non_conditioned", reason: v.reason };
  }
  if (v.kind === "mep_serving" && typeof v.reason === "string") {
    return { kind: "mep_serving", reason: v.reason };
  }
  return null;
}

/** Occupancy provenance (T1): one of the known source strings, else null.
 *  Advisory only — drives the SpacePanel badge. Unknown/absent → null. */
function occupancySource(v: unknown): ApiSpace["occupancy_source"] {
  return v === "inferred" || v === "conflicting" || v === "struct_gap_fill" || v === "confirmed"
    ? v
    : null;
}

function dropPairs(v: unknown): [string, string][] {
  if (!Array.isArray(v)) return [];
  const out: [string, string][] = [];
  for (const entry of v) {
    if (
      Array.isArray(entry) &&
      entry.length === 2 &&
      typeof entry[0] === "string" &&
      typeof entry[1] === "string"
    ) {
      out.push([entry[0], entry[1]]);
    }
  }
  return out;
}

export function guardSpace(v: unknown): ApiSpace {
  if (!isRecord(v) || typeof v.id !== "string") {
    throw new ApiShapeError("space", v);
  }
  return {
    id: v.id,
    ifc_id: numOrNull(v.ifc_id),
    global_id: strOrNull(v.global_id),
    name: str(v.name, ""),
    long_name: strOrNull(v.long_name),
    storey_ifc_id: numOrNull(v.storey_ifc_id),
    storey_name: strOrNull(v.storey_name),
    area_m2: numOrNull(v.area_m2),
    footprint: footprint(v.footprint),
    occupancy: strOrNull(v.occupancy),
    occupancy_override: strOrNull(v.occupancy_override),
    effective_occupancy: strOrNull(v.effective_occupancy),
    occupancy_source: occupancySource(v.occupancy_source),
    conditioning_class: conditioningClass(v.conditioning_class),
    is_exit: bool(v.is_exit, false),
    exclusion: exclusion(v.exclusion),
    ambient_dba: numOrNull(v.ambient_dba),
    ceiling_height_m: numOrNull(v.ceiling_height_m),
    ceiling_height_source: strOrNull(v.ceiling_height_source),
    subtypes: stringMap(v.subtypes),
    ...(Array.isArray(v.subtypes_dropped) && v.subtypes_dropped.length > 0
      ? { subtypes_dropped: dropPairs(v.subtypes_dropped) }
      : {}),
  };
}

export function guardSpaces(v: unknown): ApiSpace[] {
  if (!Array.isArray(v)) throw new ApiShapeError("space list", v);
  return v.map(guardSpace);
}

export function guardStorey(v: unknown): ApiStorey {
  if (!isRecord(v) || typeof v.id !== "string" || typeof v.ifc_id !== "number") {
    throw new ApiShapeError("storey", v);
  }
  return {
    id: v.id,
    ifc_id: v.ifc_id,
    global_id: strOrNull(v.global_id),
    name: str(v.name, ""),
    elevation: numOrNull(v.elevation),
  };
}

export function guardStoreys(v: unknown): ApiStorey[] {
  if (!Array.isArray(v)) throw new ApiShapeError("storey list", v);
  return v.map(guardStorey);
}

export function guardAutoAssign(v: unknown): AutoAssignResult {
  if (!isRecord(v) || !Array.isArray(v.spaces)) {
    throw new ApiShapeError("auto-assign response", v);
  }
  return {
    seed: safeSeed(v.seed) ?? 0,
    occupancy_assigned: numOrNull(v.occupancy_assigned) ?? 0,
    exits_marked: numOrNull(v.exits_marked) ?? 0,
    spaces: v.spaces.map(guardSpace),
    readiness: guardReadiness(v.readiness),
  };
}

function readinessStatus(v: unknown): ReadinessStatus {
  return READINESS_STATUSES.includes(v as ReadinessStatus) ? (v as ReadinessStatus) : "missing";
}

function guardCapability(v: unknown): ReadinessCapability {
  if (!isRecord(v) || typeof v.key !== "string") {
    throw new ApiShapeError("readiness capability", v);
  }
  const confidence = numOrNull(v.confidence) ?? 0;
  return {
    key: v.key,
    status: readinessStatus(v.status),
    confidence: Math.min(1, Math.max(0, confidence)),
    detail: str(v.detail, ""),
  };
}

function guardGate(v: unknown): GenerationGate {
  if (!isRecord(v) || typeof v.kind !== "string") {
    throw new ApiShapeError("generation gate", v);
  }
  return {
    kind: v.kind,
    // Default to disabled — never confident-wrong "enabled".
    enabled: bool(v.enabled, false),
    blocked_by: stringArray(v.blocked_by),
    blocked_by_quality: stringArray(v.blocked_by_quality),
  };
}

/**
 * Non-negative integer count, defaulting to `0` for anything else (absent,
 * wrong type, negative, non-integer) — never throws, since a malformed
 * count must not make the whole readiness report unusable.
 */
function count(v: unknown): number {
  const n = numOrNull(v);
  return n !== null && Number.isInteger(n) && n >= 0 ? n : 0;
}

/**
 * Tolerates the field being absent entirely (older server, or a
 * boon-produced payload predating this field) by defaulting to
 * `{ spaces: 0, openings: 0 }` rather than throwing.
 */
function exclusionCounts(v: unknown): ExclusionCounts {
  if (!isRecord(v)) return { spaces: 0, openings: 0 };
  return { spaces: count(v.spaces), openings: count(v.openings) };
}

export function guardReadiness(v: unknown): ReadinessReport {
  if (!isRecord(v) || !Array.isArray(v.capabilities)) {
    throw new ApiShapeError("readiness report", v);
  }
  return {
    capabilities: v.capabilities.map(guardCapability),
    generation: Array.isArray(v.generation) ? v.generation.map(guardGate) : [],
    exclusions: exclusionCounts(v.exclusions),
  };
}

function diffEntry(v: unknown): DiffEntry {
  if (!isRecord(v) || typeof v.key !== "string") {
    throw new ApiShapeError("diff entry", v);
  }
  return {
    key: v.key,
    name: str(v.name, ""),
    // serde skips None hashes entirely — absent means null here.
    prev_hash: strOrNull(v.prev_hash),
    new_hash: strOrNull(v.new_hash),
  };
}

function diffEntries(v: unknown): DiffEntry[] {
  if (!Array.isArray(v)) return [];
  return v.map(diffEntry);
}

export function guardDiff(v: unknown): ModelDiff {
  if (!isRecord(v) || !isRecord(v.diff)) throw new ApiShapeError("model diff", v);
  return {
    model_id: str(v.model_id, ""),
    prev_model_id: str(v.prev_model_id, ""),
    diff: {
      added: diffEntries(v.diff.added),
      removed: diffEntries(v.diff.removed),
      changed: diffEntries(v.diff.changed),
      unchanged: stringArray(v.diff.unchanged),
    },
  };
}

// ---------------------------------------------------------------------------
// Wave 3 — MEP generation (pinned contract)
// ---------------------------------------------------------------------------

export function guardGenerateAccepted(v: unknown): GenerateAccepted {
  if (
    !isRecord(v) ||
    typeof v.job_id !== "string" ||
    typeof v.mep_system_id !== "string" ||
    safeSeed(v.seed) === null
  ) {
    throw new ApiShapeError("generate response", v);
  }
  return { job_id: v.job_id, mep_system_id: v.mep_system_id, seed: v.seed as number };
}

export function guardMepSystem(v: unknown): MepSystem {
  if (!isRecord(v) || typeof v.id !== "string") {
    throw new ApiShapeError("mep system", v);
  }
  return {
    id: v.id,
    model_id: str(v.model_id, ""),
    discipline: str(v.discipline, ""),
    status: str(v.status, ""),
    // Unsafe seeds default to 0 — system.seed feeds the pin/echo path.
    seed: safeSeed(v.seed) ?? 0,
    ruleset_hash: strOrNull(v.ruleset_hash),
    generated_at: numOrNull(v.generated_at),
  };
}

export function guardMepSystems(v: unknown): MepSystem[] {
  if (!Array.isArray(v)) throw new ApiShapeError("mep system list", v);
  return v.map(guardMepSystem);
}

function guardDevice(v: unknown): MepDevice {
  if (!isRecord(v) || typeof v.id !== "string" || typeof v.kind !== "string") {
    throw new ApiShapeError("device", v);
  }
  return {
    id: v.id,
    kind: v.kind,
    catalog_item_id: strOrNull(v.catalog_item_id),
    storey_id: strOrNull(v.storey_id),
    space_id: strOrNull(v.space_id),
    x_m: numOrNull(v.x_m) ?? 0,
    y_m: numOrNull(v.y_m) ?? 0,
    z_m: numOrNull(v.z_m) ?? 0,
    mount: strOrNull(v.mount),
    candela: numOrNull(v.candela),
    dba_at_10ft: numOrNull(v.dba_at_10ft),
    circuit_id: strOrNull(v.circuit_id),
    zone_id: strOrNull(v.zone_id),
    origin: strOrNull(v.origin),
  };
}

export function guardDevices(v: unknown): MepDevice[] {
  if (!isRecord(v) || !Array.isArray(v.devices)) {
    throw new ApiShapeError("device list", v);
  }
  return v.devices.map(guardDevice);
}

function guardZone(v: unknown): MepZone {
  if (!isRecord(v) || typeof v.id !== "string") {
    throw new ApiShapeError("zone", v);
  }
  return {
    id: v.id,
    storey_id: strOrNull(v.storey_id),
    space_ids: stringArray(v.space_ids),
    area_m2: numOrNull(v.area_m2),
  };
}

export function guardZones(v: unknown): MepZone[] {
  if (!isRecord(v) || !Array.isArray(v.zones)) {
    throw new ApiShapeError("zone list", v);
  }
  return v.zones.map(guardZone);
}

function guardCircuit(v: unknown): MepCircuit {
  if (!isRecord(v) || typeof v.id !== "string") {
    throw new ApiShapeError("circuit", v);
  }
  return {
    id: v.id,
    kind: str(v.kind, ""),
    device_ids: stringArray(v.device_ids),
    wire_length_m: numOrNull(v.wire_length_m),
    worst_voltage_v: numOrNull(v.worst_voltage_v),
    // Default to failing — never confident-wrong "ok".
    ok: bool(v.ok, false),
  };
}

export function guardCircuits(v: unknown): MepCircuit[] {
  if (!isRecord(v) || !Array.isArray(v.circuits)) {
    throw new ApiShapeError("circuit list", v);
  }
  return v.circuits.map(guardCircuit);
}

// ---------------------------------------------------------------------------
// Wave 4 — HVAC ductwork (pinned contract)
// ---------------------------------------------------------------------------

/** [[x, y, z], ...] centerline; throws when fewer than 2 valid points. */
function centerline(v: unknown): [number, number, number][] {
  if (!Array.isArray(v)) throw new ApiShapeError("duct centerline", v);
  const points: [number, number, number][] = [];
  for (const p of v) {
    if (
      !Array.isArray(p) ||
      typeof p[0] !== "number" ||
      typeof p[1] !== "number" ||
      typeof p[2] !== "number"
    ) {
      throw new ApiShapeError("duct centerline point", p);
    }
    points.push([p[0], p[1], p[2]]);
  }
  if (points.length < 2) throw new ApiShapeError("duct centerline", v);
  return points;
}

function ductSystem(v: unknown): DuctSystem {
  if (!DUCT_SYSTEMS.includes(v as DuctSystem)) throw new ApiShapeError("duct system", v);
  return v as DuctSystem;
}

function ductShape(v: unknown): DuctShape {
  // Unknown shapes degrade to round (a centerline is still renderable).
  return DUCT_SHAPES.includes(v as DuctShape) ? (v as DuctShape) : "round";
}

function ductRole(v: unknown, system: DuctSystem): DuctRole {
  if (DUCT_ROLES.includes(v as DuctRole)) return v as DuctRole;
  // Unknown roles degrade to the system's "branch" default so older payloads
  // (or hand-rolled fixtures) still render.
  return system === "return" ? "return_branch" : "branch";
}

function guardDuct(v: unknown): MepDuct {
  if (!isRecord(v) || typeof v.id !== "string") {
    throw new ApiShapeError("duct", v);
  }
  const system = ductSystem(v.system);
  return {
    id: v.id,
    storey_id: strOrNull(v.storey_id),
    system,
    role: ductRole(v.role, system),
    centerline: centerline(v.centerline),
    shape: ductShape(v.shape),
    diameter_m: numOrNull(v.diameter_m),
    width_m: numOrNull(v.width_m),
    height_m: numOrNull(v.height_m),
    cfm: numOrNull(v.cfm),
    velocity_fpm: numOrNull(v.velocity_fpm),
  };
}

export function guardDucts(v: unknown): MepDuct[] {
  if (!isRecord(v) || !Array.isArray(v.ducts)) {
    throw new ApiShapeError("duct list", v);
  }
  return v.ducts.map(guardDuct);
}

function guardFitting(v: unknown): MepFitting {
  if (!isRecord(v) || typeof v.id !== "string" || typeof v.kind !== "string") {
    throw new ApiShapeError("fitting", v);
  }
  return {
    id: v.id,
    kind: v.kind,
    x_m: numOrNull(v.x_m) ?? 0,
    y_m: numOrNull(v.y_m) ?? 0,
    z_m: numOrNull(v.z_m) ?? 0,
    duct_ids: stringArray(v.duct_ids),
  };
}

export function guardFittings(v: unknown): MepFitting[] {
  if (!isRecord(v) || !Array.isArray(v.fittings)) {
    throw new ApiShapeError("fitting list", v);
  }
  return v.fittings.map(guardFitting);
}

function guardTerminal(v: unknown): MepTerminal {
  if (!isRecord(v) || typeof v.id !== "string" || typeof v.kind !== "string") {
    throw new ApiShapeError("terminal", v);
  }
  return {
    id: v.id,
    kind: v.kind,
    catalog_item_id: strOrNull(v.catalog_item_id),
    space_id: strOrNull(v.space_id),
    storey_id: strOrNull(v.storey_id),
    x_m: numOrNull(v.x_m) ?? 0,
    y_m: numOrNull(v.y_m) ?? 0,
    z_m: numOrNull(v.z_m) ?? 0,
    neck_in: numOrNull(v.neck_in),
    cfm: numOrNull(v.cfm),
  };
}

export function guardTerminals(v: unknown): MepTerminal[] {
  if (!isRecord(v) || !Array.isArray(v.terminals)) {
    throw new ApiShapeError("terminal list", v);
  }
  return v.terminals.map(guardTerminal);
}

const EQUIPMENT_KINDS: readonly EquipmentKind[] = ["air_handling_unit"] as const;

function equipmentKind(v: unknown): EquipmentKind {
  return EQUIPMENT_KINDS.includes(v as EquipmentKind) ? (v as EquipmentKind) : "air_handling_unit";
}

function guardEquipmentRow(v: unknown): MepEquipment {
  if (!isRecord(v) || typeof v.id !== "string") {
    throw new ApiShapeError("equipment", v);
  }
  return {
    id: v.id,
    kind: equipmentKind(v.kind),
    catalog_item_id: str(v.catalog_item_id, ""),
    space_id: str(v.space_id, ""),
    storey_id: str(v.storey_id, ""),
    x_m: numOrNull(v.x_m) ?? 0,
    y_m: numOrNull(v.y_m) ?? 0,
    z_m: numOrNull(v.z_m) ?? 0,
    screening_only: v.screening_only === true,
  };
}

export function guardEquipment(v: unknown): MepEquipment[] {
  if (!isRecord(v) || !Array.isArray(v.equipment)) {
    throw new ApiShapeError("equipment list", v);
  }
  return v.equipment.map(guardEquipmentRow);
}

function guardAirflow(v: unknown): MepAirflow {
  if (!isRecord(v) || typeof v.space_id !== "string") {
    throw new ApiShapeError("airflow", v);
  }
  return {
    space_id: v.space_id,
    cfm: numOrNull(v.cfm) ?? 0,
    method: str(v.method, ""),
    citation: citation(v.citation),
  };
}

export function guardAirflows(v: unknown): MepAirflow[] {
  if (!isRecord(v) || !Array.isArray(v.airflows)) {
    throw new ApiShapeError("airflow list", v);
  }
  return v.airflows.map(guardAirflow);
}

// ---------------------------------------------------------------------------
// Wave 5 — codes monitor (scraper, monitor-only)
// ---------------------------------------------------------------------------

export function guardScraperSource(v: unknown): ScraperSource {
  if (!isRecord(v) || typeof v.id !== "string") {
    throw new ApiShapeError("scraper source", v);
  }
  return {
    id: v.id,
    jurisdiction: str(v.jurisdiction, ""),
    url: str(v.url, ""),
    css_selector_scope: strOrNull(v.css_selector_scope),
    last_hash: strOrNull(v.last_hash),
    last_checked_ms: numOrNull(v.last_checked_ms),
    etag: strOrNull(v.etag),
    last_modified: strOrNull(v.last_modified),
    created_at: numOrNull(v.created_at) ?? 0,
    updated_at: numOrNull(v.updated_at) ?? 0,
  };
}

export function guardScraperSources(v: unknown): ScraperSource[] {
  // The server answers a bare array (no {sources} wrapper).
  if (!Array.isArray(v)) {
    throw new ApiShapeError("scraper source list", v);
  }
  return v.map(guardScraperSource);
}

export function guardScrapeRun(v: unknown): ScrapeRunAccepted {
  // POST /api/scraper/run answers 202 with the job row (JobDto).
  if (!isRecord(v) || typeof v.id !== "string") {
    throw new ApiShapeError("scrape run job", v);
  }
  return { id: v.id };
}

function reviewStatus(v: unknown): ReviewStatus {
  // Unknown statuses degrade to "open" — never silently resolved.
  return REVIEW_STATUSES.includes(v as ReviewStatus) ? (v as ReviewStatus) : "open";
}

export function guardReviewItem(v: unknown): ReviewItem {
  if (!isRecord(v) || typeof v.id !== "string") {
    throw new ApiShapeError("review item", v);
  }
  return {
    id: v.id,
    source_id: str(v.source_id, ""),
    detected_ms: numOrNull(v.detected_ms) ?? 0,
    prev_hash: strOrNull(v.prev_hash),
    new_hash: str(v.new_hash, ""),
    diff_summary: str(v.diff_summary, ""),
    status: reviewStatus(v.status),
    notes: strOrNull(v.notes),
    created_at: numOrNull(v.created_at) ?? 0,
    updated_at: numOrNull(v.updated_at) ?? 0,
  };
}

export function guardReviewItems(v: unknown): ReviewItem[] {
  // The server answers a bare array (no {review_items} wrapper).
  if (!Array.isArray(v)) {
    throw new ApiShapeError("review item list", v);
  }
  return v.map(guardReviewItem);
}

function findingSeverity(v: unknown): FindingSeverity {
  // Unknown severities degrade to "error", never silently downgraded.
  return FINDING_SEVERITIES.includes(v as FindingSeverity) ? (v as FindingSeverity) : "error";
}

function citation(v: unknown): FindingCitation | null {
  if (!isRecord(v) || typeof v.document !== "string" || typeof v.section !== "string") {
    return null;
  }
  return {
    document: v.document,
    edition: strOrNull(v.edition),
    section: v.section,
  };
}

function measuredValue(v: unknown): string | number | null {
  if (typeof v === "string") return v;
  return numOrNull(v);
}

function guardFinding(v: unknown): ApiFinding {
  if (!isRecord(v) || typeof v.id !== "string") {
    throw new ApiShapeError("finding", v);
  }
  return {
    id: v.id,
    severity: findingSeverity(v.severity),
    rule_id: strOrNull(v.rule_id),
    citation: citation(v.citation),
    message: str(v.message, ""),
    measured: measuredValue(v.measured),
    required: measuredValue(v.required),
    space_id: strOrNull(v.space_id),
    device_id: strOrNull(v.device_id),
    overridden: bool(v.overridden, false),
    override_reason: strOrNull(v.override_reason),
  };
}

export function guardFindings(v: unknown): ApiFinding[] {
  if (!isRecord(v) || !Array.isArray(v.findings)) {
    throw new ApiShapeError("finding list", v);
  }
  return v.findings.map(guardFinding);
}
